import React, { FC, useState } from 'react';
import { faEdit, Icon } from '@/ui/components';
import { useTranslate } from '@/translations';
import {
  useInspectionsChecklistDynamicOperations,
  useInspectionsChecklistElectricHybrid,
  useInspectionsChecklistEngineRunning,
  useInspectionsChecklistInterior,
} from '@/api/Inspections';
import { useAppCustomers } from '@/hooks';
import { TranslationKey } from '@/translations/types';
import { isValidDate, localDateTimeFormatted } from '@/utils/helpers';

import { Button, Collapse, Descriptions, Spin } from 'antd';
import { InspectionPageTab } from '../types';
import { formatChecklistLabel, formatChecklistValue } from './helpers';
import { UpdateMechanicalInspectionPopup } from './UpdateMechanicalInspectionPopup';

export const Checklist: FC<InspectionPageTab> = ({ inspection }) => {
  const { t } = useTranslate();
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const { hasMechanicalUpdateAccess } = useAppCustomers();
  const { isFetching: dynamicOperationsLoading, data: dynamicOperationsData } =
    useInspectionsChecklistDynamicOperations(inspection.id);
  const { isFetching: electricHybridLoading, data: electricHybridData } =
    useInspectionsChecklistElectricHybrid(inspection.id);
  const { isFetching: engineRunningLoading, data: engineRunningData } =
    useInspectionsChecklistEngineRunning(inspection.id);
  const { isFetching: interiorLoading, data: interiorData } =
    useInspectionsChecklistInterior(inspection.id);

  const dynamicOperations = dynamicOperationsData?.[0];
  const electricHybrid = electricHybridData?.entities?.[0];
  const engineRunning = engineRunningData?.entities?.[0];
  const interior = interiorData?.entities?.[0];

  const isLoading =
    dynamicOperationsLoading ||
    electricHybridLoading ||
    engineRunningLoading ||
    interiorLoading;

  const accordionItems = {
    dynamicOperations,
    engineRunning,
    electricHybrid,
    interior,
  };

  return (
    <>
      {hasMechanicalUpdateAccess ? (
        <div className="d-flex justify-content-end mb-3">
          <Button type="primary" onClick={() => setIsEditPopupOpen(true)}>
            <Icon icon={faEdit} className="me-2" />
            {t('updateMechanicalInspection')}
          </Button>
        </div>
      ) : null}
      <Spin spinning={isLoading}>
        <Collapse
          defaultActiveKey={Object.keys(accordionItems).filter(
            (key) => !!accordionItems[key as keyof typeof accordionItems],
          )}
          items={Object.entries(accordionItems).map(([endpoint, data]) => {
            const needToHighlightBlockWithFailedCheck = Object.values(
              data || {},
            ).some((value) => value === 'Fail');

            return {
              key: endpoint,
              label: t(endpoint as TranslationKey),
              className: needToHighlightBlockWithFailedCheck
                ? 'highlight-block'
                : '',
              children: data ? (
                <Descriptions
                  column={1}
                  size="small"
                  bordered
                  items={Object.entries(data)
                    .filter(([key]) => key !== 'id')
                    .map(([question, answer]) => {
                      const formattedQuestion = formatChecklistLabel(question);
                      let formattedAnswer = answer;

                      if (typeof answer === 'string') {
                        formattedAnswer = formatChecklistValue(answer);
                      }

                      if (
                        question.toLowerCase().includes('date') &&
                        isValidDate(answer)
                      ) {
                        formattedAnswer = localDateTimeFormatted(answer);
                      }

                      return {
                        key: question,
                        label: formattedQuestion,
                        children: formattedAnswer,
                      };
                    })}
                />
              ) : null,
            };
          })}
        />
      </Spin>
      <UpdateMechanicalInspectionPopup
        inspection={inspection}
        open={isEditPopupOpen}
        onClose={() => setIsEditPopupOpen(false)}
        dynamicOperations={dynamicOperations}
        engineRunning={engineRunning}
        electricHybrid={electricHybrid}
        interior={interior}
      />
    </>
  );
};
